import type { Session } from '../types';

const AUTH_URL =
  process.env.REACT_APP_N8N_AUTH_URL ||
  'https://apiwhattsdom-n8n.znwkmn.easypanel.host/webhook/78fd29a4-4c21-4abd-b45d-b0a49f27ca85';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

interface LoginResponse {
  error: 'true' | 'false' | string;
  id?: string | null;
  token?: string;
  name?: string;
  phone?: string;
  open?: string | number | boolean | null;
  usa_extracao_google?: string | number | boolean | null;
  instance?: string;
  api_key_serp?: string | null;
  apikey?: string | null;
  api_key_apolo?: string | null;
  data?: {
    google_calendarid?: string | null;
  };
}

type LoginApiResponse = LoginResponse | LoginResponse[];

interface RegisterResponse {
  success?: string;
  error?: string;
  id?: string | null;
  token: string | null;
  name: string | null;
  phone: string | null;
}

interface UserProfileResponse {
  id?: string;
  email?: string | null;
  name?: string | null;
  phone?: string | null;
  open?: string | number | boolean | null;
  usa_extracao_google?: string | number | boolean | null;
  instance?: string | null;
  apikey?: string | null;
  google_calendarid?: string | null;
  api_key_serp?: string | null;
  api_key_apolo?: string | null;
}

type UserProfileRawResponse = UserProfileResponse | UserProfileResponse[];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const cleanError = (msg?: string) => (msg || '').replace(/\n/g, '').trim();
const safeValue = (value?: string | null) => {
  const sanitized = (value || '').trim();
  if (!sanitized || sanitized.toLowerCase() === 'undefined' || sanitized.toLowerCase() === 'null') return '';
  return sanitized;
};

const parseBooleanish = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toLowerCase();
  if (['1', 'true', 'sim', 'yes', 'on'].includes(normalized)) return true;
  if (['0', 'false', 'nao', 'não', 'no', 'off'].includes(normalized)) return false;
  return null;
};

const extractErrorMessage = (payload: unknown, fallback = 'Não foi possível concluir a operação.') => {
  if (typeof payload === 'string') return cleanError(payload) || fallback;
  if (!payload || typeof payload !== 'object') return fallback;

  const asRecord = payload as Record<string, unknown>;
  const candidates = [asRecord.error, asRecord.message, asRecord.detail, asRecord.msg];
  for (const item of candidates) {
    if (typeof item === 'string' && cleanError(item)) return cleanError(item);
  }
  return fallback;
};

const mapProfileUpdateError = (rawMessage: string) => {
  const message = rawMessage.toLowerCase();
  if (message.includes('users_phone_unique') || (message.includes('duplicate key') && message.includes('phone'))) {
    return 'Este telefone já está cadastrado em outra conta.';
  }
  if (message.includes('users_email_unique') || (message.includes('duplicate key') && message.includes('email'))) {
    return 'Este email já está cadastrado em outra conta.';
  }
  if (message.includes('error in workflow') || message.includes('workflow error')) {
    return 'Ocorreu um erro interno no processamento. Tente novamente em instantes.';
  }
  return rawMessage;
};

const mapRegisterError = (rawMessage: string) => {
  const message = rawMessage.toLowerCase();
  if (message.includes('email') && message.includes('cadastrado')) {
    return 'Este email já está cadastrado.';
  }
  if (message.includes('phone') || message.includes('telefone')) {
    if (message.includes('cadastrado') || message.includes('duplicate')) {
      return 'Este telefone já está cadastrado.';
    }
  }
  if (message.includes('users_email_unique')) return 'Este email já está cadastrado.';
  if (message.includes('users_phone_unique')) return 'Este telefone já está cadastrado.';
  return rawMessage;
};

const toSession = (input: {
  id?: string | null;
  token: string;
  name: string;
  phone: string;
  email?: string;
  open?: string | number | boolean | null;
  usa_extracao_google?: string | number | boolean | null;
  instance?: string;
  api_key_serp?: string | null;
  apikey?: string | null;
  api_key_apolo?: string | null;
  google_calendarid?: string | null;
}): Session => ({
  token: input.token,
  user: {
    id: safeValue(input.id) || input.token,
    name: input.name,
    email: input.email,
    phone: input.phone,
    usa_extracao_google: input.usa_extracao_google ?? null,
    open: input.open ?? null,
    instance: input.instance,
    api_key_serp: input.api_key_serp ?? null,
    apikey: input.apikey ?? null,
    api_key_apolo: input.api_key_apolo ?? null,
    google_calendarid: input.google_calendarid ?? null
  }
});

const postJson = async <T>(body: Record<string, string>) => {
  const response = await fetch(AUTH_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });

  const data = (await response.json().catch(() => null)) as T | null;
  if (!data) throw new Error('Resposta inválida do servidor.');
  return data;
};

const resolveUserId = async (token: string, fallbackId?: string | null) => {
  const sanitizedFallback = safeValue(fallbackId);
  try {
    const raw = await postJson<UserProfileRawResponse>({
      route: 'get_users',
      token
    });
    const profile = normalizeUserProfileResponse(raw);
    return safeValue(profile.id) || sanitizedFallback || token;
  } catch {
    return sanitizedFallback || token;
  }
};

const normalizeLoginResponse = (raw: LoginApiResponse): LoginResponse => {
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (!first || typeof first !== 'object') throw new Error('Resposta inválida do servidor.');
    return first;
  }

  return raw;
};

const normalizeUserProfileResponse = (raw: UserProfileRawResponse): UserProfileResponse => {
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (!first || typeof first !== 'object') throw new Error('Resposta inválida do servidor.');
    return first;
  }

  return raw;
};

export const saveSession = (session: Session) => {
  localStorage.setItem(TOKEN_KEY, session.token);
  localStorage.setItem(USER_KEY, JSON.stringify(session.user));
};

export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

export const getSession = (): Session | null => {
  const token = localStorage.getItem(TOKEN_KEY);
  const rawUser = localStorage.getItem(USER_KEY);
  if (!token || !rawUser) return null;
  try {
    const user = JSON.parse(rawUser) as Session['user'];
    return { token, user };
  } catch {
    return null;
  }
};

export const authService = {
  async login(email: string, password: string): Promise<Session> {
    const safeEmail = email.trim().toLowerCase();
    const safePassword = password.trim();

    if (!emailRegex.test(safeEmail)) throw new Error('Email inválido.');
    if (safePassword.length < 1) throw new Error('Senha obrigatória.');

    const payload = {
      route: 'login',
      email: safeEmail,
      password: safePassword
    };

    const raw = await postJson<LoginApiResponse>(payload);
    const res = normalizeLoginResponse(raw);

    if (String(res.error) === 'true') {
      throw new Error('Email ou senha inválidos.');
    }

    if (String(res.error) !== 'false' || !res.token || !res.name || !res.phone) {
      throw new Error('Falha no login. Tente novamente.');
    }

    const userId = await resolveUserId(res.token, res.id);

    const session = toSession({
      id: userId,
      token: res.token,
      name: res.name,
      phone: res.phone,
      email: safeEmail,
      usa_extracao_google: res.usa_extracao_google ?? null,
      open: res.open ?? null,
      instance: res.instance,
      api_key_serp: res.api_key_serp,
      apikey: res.apikey,
      api_key_apolo: res.api_key_apolo,
      google_calendarid: res.data?.google_calendarid ?? null
    });

    saveSession(session);
    return session;
  },

  async register(payload: { email: string; password: string; phone: string; name: string }): Promise<Session> {
    const safeEmail = payload.email.trim().toLowerCase();
    const safePassword = payload.password.trim();
    const safePhone = payload.phone.trim();
    const safeName = payload.name.trim();

    if (!emailRegex.test(safeEmail)) throw new Error('Email inválido.');
    if (safePassword.length < 8) throw new Error('A senha deve ter pelo menos 8 caracteres.');
    if (safeName.length < 2) throw new Error('Nome inválido.');
    if (safePhone.length < 6) throw new Error('Telefone inválido.');

    const body = {
      route: 'cadastro',
      email: safeEmail,
      password: safePassword,
      phone: safePhone,
      name: safeName
    };

    const res = await postJson<RegisterResponse>(body);
    const errorText = cleanError(res.error);
    const successText = cleanError(res.success);
    const token = safeValue(res.token);

    if (errorText) {
      throw new Error(mapRegisterError(errorText));
    }

    if (successText && successText.toLowerCase() !== 'success') {
      throw new Error('Não foi possível concluir o cadastro.');
    }

    if (!token) {
      throw new Error('Não foi possível concluir o cadastro.');
    }

    const userId = await resolveUserId(token, res.id);

    const session = toSession({
      id: userId,
      token,
      name: res.name || safeName,
      phone: res.phone || safePhone,
      email: safeEmail
    });

    saveSession(session);
    return session;
  },

  async forgotPassword(email: string): Promise<{ ok: true; message: string }> {
    const safeEmail = email.trim().toLowerCase();
    if (!emailRegex.test(safeEmail)) throw new Error('Email inválido.');
    return { ok: true, message: `Se existir conta para ${safeEmail}, enviamos um link de recuperação.` };
  },

  async updateProfile(userId: string, payload: { name?: string; email?: string; phone?: string; password?: string }): Promise<Session> {
    const current = getSession();
    if (!current || current.user.id !== userId) throw new Error('Sessão inválida.');
    const body: Record<string, string> = {
      route: 'update_perfil',
      token: current.token
    };

    const nextName = payload.name?.trim();
    const nextEmail = payload.email?.trim().toLowerCase();
    const nextPhone = payload.phone?.trim();
    const nextPassword = payload.password?.trim();

    if (typeof nextName === 'string') body.name = nextName;
    if (typeof nextEmail === 'string') body.email = nextEmail;
    if (typeof nextPhone === 'string') body.phone = nextPhone;
    if (typeof nextPassword === 'string') body.password = nextPassword;

    const hasChanges = Object.keys(body).some((key) => key !== 'route' && key !== 'token');
    if (!hasChanges) {
      return current;
    }

    const response = await fetch(AUTH_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    const rawText = await response.text().catch(() => '');
    let parsed: unknown = null;
    if (rawText) {
      try {
        parsed = JSON.parse(rawText);
      } catch {
        parsed = rawText;
      }
    }

    if (!response.ok) {
      const extracted = extractErrorMessage(parsed, `Erro ao atualizar perfil (${response.status}).`);
      throw new Error(mapProfileUpdateError(extracted));
    }

    const nonHttpError = extractErrorMessage(parsed, '');
    if (nonHttpError && nonHttpError.toLowerCase().includes('duplicate key')) {
      throw new Error(mapProfileUpdateError(nonHttpError));
    }

    const next: Session = {
      token: current.token,
      user: {
        ...current.user,
        name: body.name ?? current.user.name,
        email: body.email ?? current.user.email,
        phone: body.phone ?? current.user.phone
      }
    };
    saveSession(next);
    return next;
  },

  async getUserProfile(userId: string): Promise<{ name: string; email: string; phone: string }> {
    const current = getSession();
    if (!current || current.user.id !== userId) throw new Error('Sessão inválida.');

    const raw = await postJson<UserProfileRawResponse>({
      route: 'get_users',
      token: current.token
    });
    const profile = normalizeUserProfileResponse(raw);

    const name = safeValue(profile.name) || current.user.name || '';
    const email = safeValue(profile.email) || current.user.email || '';
    const phone = safeValue(profile.phone) || current.user.phone || '';

    const next: Session = {
      token: current.token,
      user: {
        ...current.user,
        id: safeValue(profile.id) || current.user.id,
        name,
        email,
        phone,
        usa_extracao_google: parseBooleanish(profile.usa_extracao_google) ?? current.user.usa_extracao_google ?? null,
        open: profile.open ?? current.user.open ?? null,
        instance: safeValue(profile.instance) || current.user.instance,
        apikey: safeValue(profile.apikey) || current.user.apikey,
        google_calendarid: safeValue(profile.google_calendarid) || current.user.google_calendarid,
        api_key_serp: safeValue(profile.api_key_serp) || current.user.api_key_serp,
        api_key_apolo: safeValue(profile.api_key_apolo) || current.user.api_key_apolo
      }
    };

    saveSession(next);

    return {
      name,
      email,
      phone
    };
  },

  clearSession,
  getSession,
  saveSession
};
